/* ══ F15 STEP 3.3 / 3.7 — WHEN A COACHED WEEK IS A DELOAD, THE SERVER'S TWIN OF `CoachDeload.isDue` + `headIndex` ══════════
 * The SETTING: `coach_workout_engine.deload_every` on the ROOT workout. NULL = never. N = "every Nth week": N − 1 loading
 * weeks, then a deload. Offered 3…8 on the web; the CHECK is the same range. (What a deload DOES to a unit is `deloadDose`
 * in `coachPlan.ts`, beside the plan that calls it.)
 *
 * ⚠️ COUNTED IN THE CLIENT'S WEEKS, NOT THIS WORKOUT'S ASSIGNMENTS, so every day of one week agrees when its workouts share a
 * cadence. Weeks are MONDAY-start (`weekStart`, the same reading `due` uses). Pinned by `coachDeload.test.ts`, which carries
 * the calendars of `tools/engine-tests/CoachDeloadRuler.swift` ruler 3.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

import { isCompletedLog, isRampSet, isReconstructed, isWarmupLog, pair, weekStart, type LoggedSession, type Row }
  from "./progression.ts";

/** `CoachDeload.cadences` — one range, three readers (web select, migration CHECK, this file). */
export const DELOAD_CADENCES = [3, 4, 5, 6, 7, 8] as const;

/** `CoachDeload.weekSentence` lives in `coachPlan.ts` as `DELOAD_WEEK_SENTENCE` (the plan says it). */

/** `CoachDeload.Day` — one earlier assignment of the CLIENT (any workout): its day key and whether it was marked a deload. */
export interface DeloadDay {
  date: string;
  isDeload: boolean;
}

/** `CoachDeload.isDue` — whether the week holding `target` is a deload, by the ROOT's cadence over the CLIENT's calendar.
 *  A loading week is a calendar week holding an assignment and no marked day; the count runs from the last marked week —
 *  or, with none, from the first week — to the week before `target`. Due when it reaches N − 1, or when another day of
 *  `target`'s own week is already marked. OFF or no cadence → never. */
export function deloadIsDue(every: number | null, progress: boolean, days: DeloadDay[], target: string,
                            programMark: boolean | null = null): boolean {
  if (!progress) return false;
  /* F15 3.9 — THE PROGRAM'S MARK FIRST: a client whose week falls on a program week the coach marked (or left unmarked in a
     program that marks any week) is told by the board; the cadence below is the default for everyone else. OFF still wins:
     OFF is "the engine changes nothing", and a deload is a change. */
  if (programMark !== null) return programMark;
  if (every === null || !(DELOAD_CADENCES as readonly number[]).includes(every)) return false;
  const targetWeek = weekStart(target);
  if (days.some((d) => d.isDeload && weekStart(d.date) === targetWeek)) return true;
  /* ⚠️ Day keys compare as dates because an ISO day key sorts lexicographically exactly as it sorts chronologically. */
  const before = days.filter((d) => weekStart(d.date) < targetWeek);
  const marked = before.filter((d) => d.isDeload).map((d) => weekStart(d.date));
  const lastDeload = marked.length === 0 ? null : marked.reduce((a, b) => (b > a ? b : a));
  const loading = new Set(before.map((d) => weekStart(d.date)).filter((w) => lastDeload === null || w > lastDeload));
  return loading.size >= every - 1;
}

/** `CoachDeload.headIndex` — the most recent assignment that is NOT a marked deload (assignments most recent first), so the
 *  week after a deload is built from the last LOADING week's copy. All marked → 0; none → null. */
export function headIndex(flags: boolean[]): number | null {
  const at = flags.indexOf(false);
  return at >= 0 ? at : (flags.length === 0 ? null : 0);
}

/* ══ F15 STEP 3.9 — THE BOARD'S MARK FOR THE CLIENT'S WEEK (`docs/PROGRAMS-DESIGN.md`, DECISIONS 10-31) ═════════════════════
 * A program assigned from `start_date` (a Monday) covers weeks 1…length, length = max(week_index) over its day rows AND its
 * week rows. The newest assignment covering `target` decides. A program that marks NO week is the cadence's (null): his
 * "every Nth week" stays the default for unmarked programs. Swift twin: the phone step (QUEUE F15 3.9, next). */

export interface ProgramAssignmentRow { program_id: string; start_date: string }
export interface ProgramWeekRow { program_id: string; week_index: number; is_deload: boolean; derived: boolean }
export interface ProgramDayWeekRow { program_id: string; week_index: number }

const dayIndex = (key: string): number => Math.round(Date.parse(`${key.slice(0, 10)}T00:00:00Z`) / 86_400_000);

/** The program week `target` falls on, or null when no assigned program covers it. */
export function programWeekOf(assignments: ProgramAssignmentRow[], weeks: ProgramWeekRow[], days: ProgramDayWeekRow[],
                              target: string): { programId: string; weekIndex: number } | null {
  const lengthOf = (id: string): number => Math.max(0,
    ...weeks.filter((w) => w.program_id === id).map((w) => w.week_index),
    ...days.filter((d) => d.program_id === id).map((d) => d.week_index));
  const t = dayIndex(target);
  let best: { programId: string; weekIndex: number; start: string } | null = null;
  for (const a of assignments) {
    const offset = t - dayIndex(a.start_date);
    if (offset < 0) continue;
    const weekIndex = Math.floor(offset / 7) + 1;
    if (weekIndex > lengthOf(a.program_id)) continue;
    if (best === null || a.start_date > best.start) best = { programId: a.program_id, weekIndex, start: a.start_date };
  }
  return best === null ? null : { programId: best.programId, weekIndex: best.weekIndex };
}

/** The mark `deloadIsDue` reads first: true / false when the covering program marks any week, null otherwise. */
export function programWeekMark(assignments: ProgramAssignmentRow[], weeks: ProgramWeekRow[], days: ProgramDayWeekRow[],
                                target: string): boolean | null {
  const at = programWeekOf(assignments, weeks, days, target);
  if (at === null) return null;
  const mine = weeks.filter((w) => w.program_id === at.programId);
  if (!mine.some((w) => w.is_deload)) return null;
  return mine.some((w) => w.week_index === at.weekIndex && w.is_deload);
}

/* ══ F15 STEP 4.2 → TWO CHOICES (his 11-02 word: "just 2 options for deload. manual … or auto") ══════════════════════════
 * `coach_programs.progress · deload_every · deload_automatic`, NO migration: MANUAL = `deload_every` NULL, AUTO = any value
 * (the page writes `AUTO_EVERY`, 7). Inside a program, in this order: OFF never deloads; a day of the week already marked
 * (an accepted proposal) is a deload. MANUAL: the board's Deload switches, and nothing else — no cadence, no numbers. AUTO:
 * the switches are not read (kept, never cleared); a deload is PROPOSED (one tap) when the logged numbers fall
 * (`deloadTriggered`, any week — his "−1 if needed") or AROUND WEEK 7: the 7th program week since the last deload the client
 * took, asked once more the week after when the 7th's was not taken (his "+1"), then only the numbers until one is taken.
 * Repeats: a deload in week 7 → the next asked at 14. `deload_automatic` is no longer read (no live row held true, MEASURED
 * 11-02); Auto never writes a deload untapped. Outside a program the workout's own row decides (`deloadIsDue`), as before.
 * Swift twin: `CoachDeload.programBlockDue` / `.programVerdict` / `.weeksSinceDeload` / `.triggered`, replayed by
 * `deloadParity.test.ts`. */

/** One program's block, as `coach_programs` stores it. */
export interface ProgramBlock { progress: boolean; deloadEvery: number | null; automatic: boolean }

/** The value the page writes for AUTO (inside the column's CHECK, 3–8), and the week it proposes around. */
export const AUTO_EVERY = 7;

/** AUTO = the numbers and the week-7 proposal; MANUAL (`deload_every` NULL) = the board's switches only. */
export const isAutoBlock = (block: ProgramBlock): boolean => block.deloadEvery !== null;

/** Whether program week `weekIndex` is a deload by the block alone: a MANUAL board's marked week. AUTO fixes no week. */
export function programBlockDue(block: ProgramBlock, weeks: readonly { week_index: number; is_deload: boolean }[],
                                weekIndex: number): boolean {
  if (!block.progress || isAutoBlock(block)) return false;
  return weeks.some((w) => w.week_index === weekIndex && w.is_deload);
}

/** AUTO's calendar: the 7th program week since the last deload, and the 8th when the 7th's proposal was not taken. */
export const calendarProposes = (sinceDeload: number): boolean => sinceDeload === AUTO_EVERY || sinceDeload === AUTO_EVERY + 1;

/** Program weeks since the client's last deload INSIDE this program run (week `weekIndex` falls on `target`'s week): the
 *  week index minus the latest marked week before it, or the week index itself with none (the program's first week = 1). A
 *  marked day before the program started is not this block's. */
export function weeksSinceDeload(weekIndex: number, days: readonly DeloadDay[], target: string): number {
  const targetWeek = weekStart(target.slice(0, 10));
  const t = Date.parse(`${targetWeek}T00:00:00Z`);
  let last = 0;
  for (const d of days) {
    const w = weekStart(d.date.slice(0, 10));
    if (!d.isDeload || w >= targetWeek) continue;
    const index = weekIndex - Math.round((t - Date.parse(`${w}T00:00:00Z`)) / (7 * 86_400_000));
    if (index >= 1 && index > last) last = index;
  }
  return weekIndex - last;
}

/** The sentence the plan says first when the numbers proposed the deload, and the proposal's reason. */
export const TRIGGER_SENTENCE = "Deload: short on more than a third of the working sets two sessions running.";

/** The sentence the plan says first when AUTO's calendar proposed it. */
export const CALENDAR_SENTENCE = "Deload: seven weeks since the last one.";

/** One prescribed WORKING set (not a warm-up, its row not paused) and what was logged against it (null = nothing). */
export interface TriggerSet {
  reps: number | null; kg: number | null; seconds: number | null;
  logged: { completed: boolean; reps: number | null; kg: number | null; seconds: number | null } | null;
}

/** A set falls SHORT when it was not done (skipped, no log, a prescribed number left blank) or logged below the
 *  prescription in reps, kilos or seconds. A blank or 0 kg prescription asks for no load. */
export function shortSet(s: TriggerSet): boolean {
  const l = s.logged;
  if (l === null || !l.completed) return true;
  const below = (want: number | null, got: number | null): boolean =>
    want !== null && want > 0 && (got === null || got < want);
  return below(s.reps, l.reps) || below(s.kg, l.kg) || below(s.seconds, l.seconds);
}

/** One earlier session of the client: its day, whether it was a deload, and its working sets. */
export interface TriggerSession { date: string; isDeload: boolean; sets: TriggerSet[] }

/** Fable's rule: over the client's two most recent sessions before `target`'s week (after the last deload, each holding a
 *  working set), BOTH fell short on more than a third of their working sets. A missed day is a session whose every set is
 *  short. Fewer than two such sessions → never. */
export function deloadTriggered(sessions: readonly TriggerSession[], target: string): boolean {
  const targetWeek = weekStart(target.slice(0, 10));
  const before = sessions.filter((s) => weekStart(s.date.slice(0, 10)) < targetWeek);
  const deloads = before.filter((s) => s.isDeload).map((s) => weekStart(s.date.slice(0, 10)));
  const lastDeload = deloads.length === 0 ? null : deloads.reduce((a, b) => (b > a ? b : a));
  const recent = before
    .filter((s) => !s.isDeload && (lastDeload === null || weekStart(s.date.slice(0, 10)) > lastDeload) && s.sets.length > 0)
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
    .slice(0, 2);
  return recent.length === 2 && recent.every((s) => s.sets.filter(shortSet).length * 3 > s.sets.length);
}

/** A session's WORKING sets and their logs, from the day's rows and its logged session (`CoachDeload.triggerSets`). Working =
 *  a MAIN row, not paused, not measured in distance or rounds (a run's time is not "below"), and not a ramp set. A day with
 *  nothing logged is a MISSED day: every set unlogged. A log that cannot be paired to the rows (the order or the exercises
 *  changed), or a reconstructed one, is not read at all: the session counts for nothing rather than for a guess. */
export function triggerSets(rows: readonly Row[], logged: LoggedSession | null): TriggerSet[] {
  const anything = logged !== null && logged.rows.some((r) => r.sets.some((s) => !isWarmupLog(s)));
  if (anything && isReconstructed(logged as LoggedSession)) return [];
  const paired = anything ? pair([...rows], logged as LoggedSession) : null;
  if (anything && paired === null) return [];
  const out: TriggerSet[] = [];
  const working = rows.filter((r) => r.hidden !== true && r.sectionRole === "main" && r.measuredInDistanceOrRounds !== true)
    .sort((a, b) => a.ordinal - b.ordinal);
  for (const r of working) {
    const logs = paired?.get(r.id) ?? [];
    let i = 0;
    for (const s of r.sets) {
      if (isRampSet(s)) continue;
      const l = logs[i++];
      out.push({ reps: s.reps, kg: s.weightKg, seconds: s.timeSeconds,
                 logged: l === undefined ? null
                   : { completed: isCompletedLog(l), reps: l.reps, kg: l.weightKg, seconds: l.holdSeconds } });
    }
  }
  return out;
}

/** What the block makes of one client week inside a program (`CoachDeload.programVerdict`). OFF → nothing. A day of the
 *  week already marked (an accepted proposal) or a MANUAL board's mark → a deload. AUTO: the numbers → proposed, `byTrigger`
 *  (the plan says `TRIGGER_SENTENCE` first); else the 7th/8th week since the last deload → proposed (`CALENDAR_SENTENCE`).
 *  Nothing is ever WRITTEN a deload untapped. */
export interface ProgramVerdict { isDeload: boolean; proposed: boolean; byTrigger: boolean }

export function programVerdict(block: ProgramBlock, weeks: readonly { week_index: number; is_deload: boolean }[],
                               weekIndex: number, weekMarked: boolean, triggered: boolean,
                               sinceDeload: number): ProgramVerdict {
  const none = { isDeload: false, proposed: false, byTrigger: false };
  if (!block.progress) return none;
  if (weekMarked || programBlockDue(block, weeks, weekIndex)) return { isDeload: true, proposed: false, byTrigger: false };
  if (!isAutoBlock(block)) return none;
  if (triggered) return { isDeload: false, proposed: true, byTrigger: true };
  return calendarProposes(sinceDeload) ? { isDeload: false, proposed: true, byTrigger: false } : none;
}

/** One earlier assignment the trigger may read: its id, day and deload mark (`coach_assignments`). */
export interface TriggerCandidate { id: string; date: string; isDeload: boolean }

/** The assignments the trigger reads, MOST RECENT FIRST: before `target`'s week, not a deload, after the last deload week.
 *  The caller reads each one's sets in this order and stops at two that hold a working set (`deloadTriggered` takes two). */
export function triggerCandidates(all: readonly TriggerCandidate[], target: string): TriggerCandidate[] {
  const targetWeek = weekStart(target.slice(0, 10));
  const before = all.filter((a) => weekStart(a.date.slice(0, 10)) < targetWeek);
  const deloads = before.filter((a) => a.isDeload).map((a) => weekStart(a.date.slice(0, 10)));
  const lastDeload = deloads.length === 0 ? null : deloads.reduce((a, b) => (b > a ? b : a));
  return before.filter((a) => !a.isDeload && (lastDeload === null || weekStart(a.date.slice(0, 10)) > lastDeload))
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
